import "../../css/App.css";
import "../../css/FullEvent/FullEvent.css";
import "../../css/FullEvent/FullEventDesktop.css";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Event,
  AccountData,
  DiscussionPost,
  PromoCode,
  TicketV2,
  CustomTicketV2,
  SavedFormQuestion,
  MediaAspectRatio,
} from "@markit/common.types";
import Footer from "../../components/Footer";
import {
  getVisibleCustomTickets,
  getCanonicalMediaUrls,
} from "@markit/common.utils";
import FullEventInfo from "../../components/FullEvent/FullEventInfo";
import FullEventHeader from "../../components/FullEvent//FullEventHeader";
import TopHeader from "../../components/TopHeader";
import StickyBox from "react-sticky-box";
import CheckoutContainer from "../../components/FullEventSubComponents/CheckoutContainer";
import { useTheme } from "../../hooks/useTheme";
import OrganizersInfoDisplay from "../../components/FullEventSubComponents/OrganizersInfoDisplay";
import { HorizontalDivider } from "../../components/Dividers/HorizontalDivider";
import MediaCarouselContainer from "../../components/Media/MediaCarouselContainer";
import MediaPreviewCarousel from "../../components/Media/MediaPreviewCarousel";
import MediaExpandedItem from "../../components/Media/MediaExpandedItem";

type FullEventDesktopProps = {
  event: Event;
  cohostsList: AccountData[];
  host: AccountData | undefined;
  posts: DiscussionPost[];
  address: string;
  tixId: string;
  ticketData: TicketV2 | undefined;
  ticketToRedeem: CustomTicketV2 | undefined;
  promoterId: string;
  promoCode?: PromoCode;
  isEventAtCapacity: boolean;
  userReferrer: string;
  selectedCustomTickets: CustomTicketV2[];
  setSelectedCustomTickets: (selectedTickets: CustomTicketV2[]) => void;
  eventFormQuestions: SavedFormQuestion[];
};

const FullEventDesktop = memo(function FullEventDesktopFn(
  props: FullEventDesktopProps
) {
  const {
    event,
    cohostsList,
    host,
    posts,
    address,
    tixId,
    ticketData,
    promoterId, // promoterId
    promoCode, // promo
    isEventAtCapacity,
    ticketToRedeem,
    userReferrer,
    selectedCustomTickets,
    setSelectedCustomTickets,
    eventFormQuestions,
  } = props;
  const { theme } = useTheme();
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [expandedImageUrl, setExpandedImageUrl] = useState<string>("");

  const mediaUrls = useMemo(() => getCanonicalMediaUrls(event), [event]);

  const styles = {
    leftContainer: {
      width: 362,
      gap: 20,
    },
    rightContainer: {
      width: 508,
      gap: 30,
    },
    mediaFrame: {
      width: "100%",
      maxWidth: 362,
      aspectRatio: event.mediaAspectRatio || MediaAspectRatio._1_1,
      borderRadius: 20,
      border: `1px solid ${theme.DividerColor.borderColor}`,
      overflow: "hidden",
      position: "relative" as const,
      flexShrink: 0,
    },
    mediaImage: {
      cursor: "pointer",
    },
  };

  const hostAndCohosts: AccountData[] =
    cohostsList && host ? [host, ...cohostsList] : host ? [host] : [];

  const refContainer = useRef<HTMLDivElement>(null);

  const visibleTickets = useMemo(
    () => getVisibleCustomTickets(event.customTickets, ticketToRedeem?.id),
    [event.customTickets, ticketToRedeem?.id]
  );

  // prevent background scrolling when the checkout modal is open
  useEffect(() => {
    if (showCheckoutForm) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showCheckoutForm]);

  return (
    <div>
      {showCheckoutForm && host ? (
        <CheckoutContainer
          event={event}
          host={host}
          tixId={tixId}
          ticketData={ticketData}
          ticketToRedeem={ticketToRedeem}
          promoterId={promoterId}
          promoCode={promoCode}
          userReferrer={userReferrer}
          selectedCustomTickets={selectedCustomTickets}
          setSelectedCustomTickets={setSelectedCustomTickets}
          eventFormQuestions={eventFormQuestions}
          isEventAtCapacity={isEventAtCapacity}
          setShowCheckoutForm={setShowCheckoutForm}
          theme={theme}
        />
      ) : null}
      {/** Allow expanded image if there is just one media image */}
      {expandedImageUrl ? (
        <MediaExpandedItem
          mediaUrl={expandedImageUrl}
          onClose={() => setExpandedImageUrl("")}
          hardTheme={event.theme}
        />
      ) : null}
      <div ref={refContainer} className="WebApp" style={theme.SubComponentBG}>
        <div className="WebContainer">
          <TopHeader showRCA eventTheme={event.theme} />
          <div className="WebColumnContainer" style={{ gap: 30 }}>
            <StickyBox offsetTop={20} offsetBottom={20}>
              <div className="ColumnNormal" style={styles.leftContainer}>
                <div className="ColumnNormal" style={{ gap: 14 }}>
                  <div style={styles.mediaFrame}>
                    <MediaCarouselContainer
                      event={event}
                      selectedMediaIndex={selectedMediaIndex}
                      setSelectedMediaIndex={setSelectedMediaIndex}
                      imageStyle={styles.mediaImage}
                      onImageClick={() =>
                        setExpandedImageUrl(mediaUrls[selectedMediaIndex])
                      }
                    />
                  </div>
                  {mediaUrls.length > 1 ? (
                    <MediaPreviewCarousel
                      event={event}
                      hardTheme={event.theme}
                      onSelectMedia={setSelectedMediaIndex}
                    />
                  ) : null}
                </div>
                <div className="ColumnNormal" style={{ gap: 14 }}>
                  <span className="bodySubtext">Hosted by</span>
                  <HorizontalDivider eventTheme={event.theme} altWidth={1} />
                  {host ? (
                    <OrganizersInfoDisplay
                      host={host}
                      organizers={hostAndCohosts}
                    />
                  ) : null}
                </div>
              </div>
            </StickyBox>
            <div className="ColumnNormal" style={styles.rightContainer}>
              <FullEventHeader event={event} host={host} address={address} />
              <FullEventInfo
                event={event}
                host={host}
                posts={posts}
                address={address}
                visibleTickets={visibleTickets}
                selectedCustomTickets={selectedCustomTickets}
                setSelectedCustomTickets={setSelectedCustomTickets}
                promoCode={promoCode}
                redeemTicket={ticketData}
                setShowCheckoutForm={setShowCheckoutForm}
              />
            </div>
          </div>
        </div>
      </div>
      <Footer showRCA creatorData={host} />
    </div>
  );
});

export default FullEventDesktop;
